import { darkenColor } from '../../../../../helpers/colors';
import { OverlayLine } from '../../../../../helpers/Chart';
import {
  CbrtRangeState,
  MrcEpisode,
} from '../../../../ProfitCalculations/types';
import { tokens } from '../tokens';
import { CandleWindow } from './candles';

export type { OverlayLine };

/**
 * Horizontal cbrt-range lines — a positive and a negative break for every
 * cbrt range that overlaps the candle window. The most recent range is drawn
 * at full strength; earlier ones are dimmed.
 */
export const computeCbrtLines = (
  cbrtRangeStates: CbrtRangeState[] | undefined,
  window: CandleWindow,
): OverlayLine[] => {
  if (!cbrtRangeStates?.length || window.candles.length === 0) {
    return [];
  }
  const now = Date.now();
  // Only ranges initiated within the window — between the first and last core
  // transaction on the chart — never stale ranges started before it.
  const visibleRanges = cbrtRangeStates
    .filter(
      (state) =>
        state.timestamp >= window.start && state.timestamp <= window.end,
    )
    .sort((a, b) => a.timestamp - b.timestamp);

  const lines: OverlayLine[] = [];
  visibleRanges.forEach((rangeState, index) => {
    const isLast = index === visibleRanges.length - 1;
    const directionSign = rangeState.direction === 'up' ? 1 : -1;
    const directionColor =
      rangeState.direction === 'up' ? tokens.green : tokens.red;
    const close =
      rangeState.open + rangeState.cbrt * rangeState.open * directionSign;
    const negativeClose = rangeState.open - (close - rangeState.open);
    const lineEnd = rangeState.endTimestamp ?? now;

    lines.push({
      points: [
        { x: rangeState.timestamp, y: close },
        { x: lineEnd, y: close },
      ],
      color: isLast ? directionColor : darkenColor(directionColor, 20),
      label: `cbrt ${close.toFixed(4)}`,
      strokeWidth: 0.75,
      labelDy: -5,
    });
    lines.push({
      points: [
        { x: rangeState.timestamp, y: negativeClose },
        { x: lineEnd, y: negativeClose },
      ],
      color:
        rangeState.direction === 'up' ? `${tokens.red}66` : `${tokens.green}66`,
      label: `cbrt ${negativeClose.toFixed(4)}`,
      strokeWidth: 0.75,
      labelDy: -5,
    });
  });
  return lines;
};

/**
 * Horizontal MRC phase lines — one per history item of every episode that
 * overlaps the candle window. Used for both the standard expanded view (all
 * episodes) and the MRC deep dive (a single focused episode).
 */
export const computeMrcLines = (
  episodes: MrcEpisode[] | undefined,
  window: CandleWindow,
): OverlayLine[] => {
  if (!episodes?.length || window.candles.length === 0) {
    return [];
  }
  const now = Date.now();
  const candleWidth =
    window.candles.length > 1
      ? window.candles[1].startTimestamp - window.candles[0].startTimestamp
      : 3600_000;

  // Only episodes initiated within the window — between the first and last
  // core transaction on the chart.
  const visibleEpisodes = episodes.filter(
    (episode) =>
      episode.startTimestamp >= window.start &&
      episode.startTimestamp <= window.end,
  );

  const lines: OverlayLine[] = [];
  visibleEpisodes.forEach((episode) => {
    const baseColor = episode.orderType === 'SELL' ? tokens.red : tokens.green;
    episode.history.forEach((item) => {
      const segmentStart = item.startTimestamp;
      const rawEnd = item.endTimestamp ?? now;
      // Guarantee a visible width for point-in-time states (RESET/CANCELLED).
      const segmentEnd =
        rawEnd <= segmentStart ? segmentStart + candleWidth : rawEnd;
      const color =
        item.state === 'CANCELLED'
          ? tokens.red
          : item.state === 'INITIALISED'
          ? darkenColor(baseColor, 70)
          : item.state === 'PENDING'
          ? darkenColor(baseColor, 90)
          : baseColor;
      lines.push({
        points: [
          { x: segmentStart, y: item.startPrice },
          { x: segmentEnd, y: item.startPrice },
        ],
        color,
        label: `mrc ${item.state}`,
        strokeWidth: 1.25,
        labelDy: episode.orderType === 'SELL' ? 9 : -3,
      });
    });
  });
  return lines;
};
